import { API_ENDPOINTS, API_HEADERS } from "../constants/api";

export interface ResearchApiPost {
  id: number;
  eng_title: string | null;
  eng_caption: string | null;
  eng_thumbnail: string | null;
  eng_content?: string | null;
  eng_tags?: { tag_name_eng?: string | null }[] | null;
  eng_description?: string | null;
  created_date?: string | null;
  /** First publish date; prefer over created_date for display. */
  published_at?: string | null;
  download_file?: string | null;
}

export interface FetchResearchPostsParams {
  page?: number;
  pageSize?: number;
}

interface PaginatedResearchResponse {
  total?: number;
  results?: ResearchApiPost[];
}

export interface ResearchPostsResult {
  posts: ResearchApiPost[];
  totalCount: number;
}

const mapResponseToResult = (data: unknown): ResearchPostsResult => {
  if (Array.isArray(data)) {
    return { posts: data, totalCount: data.length };
  }

  const paginated = data as PaginatedResearchResponse;
  if (Array.isArray(paginated?.results)) {
    return {
      posts: paginated.results,
      totalCount:
        typeof paginated.total === "number"
          ? paginated.total
          : paginated.results.length,
    };
  }

  return { posts: [], totalCount: 0 };
};

export const fetchResearchPosts = async (
  params: FetchResearchPostsParams = {}
): Promise<ResearchPostsResult> => {
  const url = new URL(API_ENDPOINTS.researchPosts);

  if (params.page) {
    url.searchParams.set("page", params.page.toString());
  }

  if (params.pageSize) {
    url.searchParams.set("pageSize", params.pageSize.toString());
  }

  const response = await fetch(url.toString(), {
    headers: API_HEADERS,
  });

  if (!response.ok) {
    throw new Error(
      `Research posts request failed with status ${response.status}`
    );
  }

  const data: unknown = await response.json();
  return mapResponseToResult(data);
};

export const fetchResearchPostById = async (
  id: number | string
): Promise<ResearchApiPost> => {
  const response = await fetch(API_ENDPOINTS.initiativeDetail(id), {
    headers: API_HEADERS,
  });

  if (!response.ok) {
    throw new Error(
      `Research post detail request failed with status ${response.status}`
    );
  }

  const data: ResearchApiPost = await response.json();
  return data;
};
